import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import PlayerSearchInput from '../components/PlayerSearchInput'
import CourseSearch, { courseSubtitle } from '../components/CourseSearch'
import { serializeRoster, nineSetForSave, resizeOtherPlayers } from '../lib/scheduledRound'

export default function ScheduleRoundPage() {
  const { player } = useAuth()
  const navigate = useNavigate()

  const [courseName, setCourseName] = useState('')
  const [selectedCourse, setSelectedCourse] = useState(null)
  const [date, setDate] = useState('')
  const [teeTime, setTeeTime] = useState('')
  const [holes, setHoles] = useState('18')
  // Which nine for a 9-hole schedule. Only captured/sent when holes === '9';
  // carries into round-setup so the user doesn't re-pick front/back on start.
  const [nineSet, setNineSet] = useState('front')
  const [playerCount, setPlayerCount] = useState(1)
  const [addFriends, setAddFriends] = useState(false)
  const [otherPlayers, setOtherPlayers] = useState([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const pickCourse = (c) => {
    setSelectedCourse(c)
    setCourseName(c.name)
    // Match the start-round flow: a 9-hole course defaults the holes selection to 9.
    if ((c.holes_data?.length || c.holes || 18) <= 9) setHoles('9')
  }

  const updatePlayerCount = (n) => {
    setPlayerCount(n)
    if (n > 1) {
      // Preserve entered players (full row shape) when the count changes.
      setOtherPlayers(prev => resizeOtherPlayers(prev, n))
    } else {
      setOtherPlayers([])
      setAddFriends(false)
    }
  }

  const todayStr = new Date().toISOString().split('T')[0]

  const canSave = selectedCourse && date && teeTime

  const handleSchedule = async () => {
    if (!canSave) return
    setSaving(true)
    setError(null)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const token = session?.access_token
      if (!token) {
        setError('Not signed in. Please log in again.')
        setSaving(false)
        return
      }
      const res = await fetch('/.netlify/functions/scheduled-rounds', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          course_id: selectedCourse.id,
          date,
          tee_time: teeTime || null,
          holes: parseInt(holes),
          player_count: playerCount,
          // F-079: persist the selected friends + which-nine so "Start round"
          // pre-populates them instead of the user re-entering everything.
          roster: serializeRoster(otherPlayers),
          nine_set: nineSetForSave(holes, nineSet),
        }),
      })
      if (!res.ok) {
        const text = await res.text()
        setError(`Save failed (${res.status}): ${text}`)
        setSaving(false)
        return
      }
      navigate('/')
    } catch (e) {
      setError(`Network error: ${e.message}`)
      setSaving(false)
    }
  }

  return (
    <div className="page">
      <div className="container">
        <div style={{ paddingTop: 8, marginBottom: 20 }}>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => navigate(-1)}
            style={{ marginBottom: 12, padding: '6px 12px' }}
          >
            ← Back
          </button>
          <h1 style={{ fontSize: 'clamp(1.4rem, 4vw, 1.8rem)' }}>Schedule a Round</h1>
          <p className="text-muted text-sm" style={{ marginTop: 4 }}>
            Set your course, date, and tee time.
          </p>
        </div>

        {/* Course search */}
        <div style={{ marginBottom: 20 }}>
          <label>Course</label>
          {!selectedCourse ? (
            <CourseSearch onSelect={pickCourse} />
          ) : (
            <div style={{
              padding: '10px 12px',
              background: 'var(--green-dark)', borderRadius: 'var(--radius)',
              border: '1px solid var(--gold)', fontSize: '0.85rem',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
            }}>
              <span style={{ minWidth: 0 }}>
                <strong style={{ display: 'block' }}>{selectedCourse.club_name || selectedCourse.name}</strong>
                {courseSubtitle(selectedCourse) && (
                  <span className="text-muted" style={{ display: 'block', fontSize: '0.78rem', marginTop: 2 }}>
                    {courseSubtitle(selectedCourse)}
                  </span>
                )}
              </span>
              <button
                onClick={() => { setSelectedCourse(null); setCourseName('') }}
                style={{ background: 'none', border: 'none', color: 'var(--gray-500)', cursor: 'pointer', fontSize: '0.8rem', flexShrink: 0 }}
              >
                Change
              </button>
            </div>
          )}
        </div>

        {/* Date */}
        <div style={{ marginBottom: 20 }}>
          <label>Date</label>
          <input
            className="input"
            type="date"
            value={date}
            min={todayStr}
            onChange={e => setDate(e.target.value)}
          />
        </div>

        {/* Tee time */}
        <div style={{ marginBottom: 20 }}>
          <label>Tee Time</label>
          <input
            className="input"
            type="time"
            value={teeTime}
            onChange={e => setTeeTime(e.target.value)}
          />
        </div>

        {/* Holes */}
        <div style={{ marginBottom: 20 }}>
          <label>Holes</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {['9', '18'].map(h => (
              <button
                key={h}
                onClick={() => setHoles(h)}
                className={`btn ${holes === h ? 'btn-primary' : 'btn-ghost'}`}
                style={{ flex: 1 }}
              >
                {h} Holes
              </button>
            ))}
          </div>
          {/* Front/Back — only when scheduling 9 holes. Carries into round-setup
              (F-079) so the user doesn't re-pick which nine on Start. */}
          {holes === '9' && (
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              {[
                { id: 'front', label: 'Front 9' },
                { id: 'back', label: 'Back 9' },
              ].map(opt => (
                <button
                  key={opt.id}
                  onClick={() => setNineSet(opt.id)}
                  className={`btn ${nineSet === opt.id ? 'btn-primary' : 'btn-ghost'}`}
                  style={{ flex: 1 }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Player count */}
        <div style={{ marginBottom: playerCount > 1 ? 12 : 24 }}>
          <label>Players in Group</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {[1, 2, 3, 4, 5].map(n => (
              <button
                key={n}
                onClick={() => updatePlayerCount(n)}
                className={`btn ${playerCount === n ? 'btn-primary' : 'btn-ghost'}`}
                style={{ flex: 1 }}
              >
                {n}
              </button>
            ))}
          </div>
        </div>

        {/* Add friends */}
        {playerCount > 1 && !addFriends && (
          <div style={{ marginBottom: 24 }}>
            <button
              className="btn btn-ghost btn-full"
              onClick={() => setAddFriends(true)}
              style={{ fontSize: '0.9rem', padding: '12px 16px' }}
            >
              Add friends now?
            </button>
          </div>
        )}

        {playerCount > 1 && addFriends && (
          <div style={{ marginBottom: 24 }}>
            <label>Other Players</label>
            {otherPlayers.map((p, i) => (
              <PlayerSearchInput
                key={i}
                index={i}
                value={p}
                playerId={player?.id}
                onChange={val => {
                  setOtherPlayers(prev => {
                    const arr = [...prev]
                    arr[i] = val
                    return arr
                  })
                }}
              />
            ))}
          </div>
        )}

        {error && (
          <div style={{
            padding: '10px 12px', marginBottom: 12,
            background: 'rgba(220,50,50,0.1)', border: '1px solid rgba(220,50,50,0.4)',
            borderRadius: 'var(--radius)', color: '#ff8080', fontSize: '0.85rem',
          }}>
            {error}
          </div>
        )}

        <button
          className="btn btn-primary btn-full"
          onClick={handleSchedule}
          disabled={!canSave || saving}
          style={{ padding: '16px 24px', fontSize: '1rem', fontWeight: 600 }}
        >
          {saving ? 'Scheduling...' : 'Schedule Round'}
        </button>
      </div>
    </div>
  )
}
